import React, { useState, useRef, useEffect } from 'react';

interface TimecodeInputProps {
  value: number | undefined; // seconds
  onChange: (seconds: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  fps?: number;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const TimecodeInput: React.FC<TimecodeInputProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  fps = 24,
  className = '',
  placeholder = '00:00:00:00',
  disabled = false
}) => {
  const [localValue, setLocalValue] = useState<string>('');
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const repeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const repeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isKeyHeldRef = useRef<boolean>(false);

  // Format seconds to timecode string HH:MM:SS:FF
  const formatTimecode = (seconds: number | undefined): string => {
    if (seconds === undefined) return '00:00:00:00';
    const totalSeconds = Number(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);
    const frames = Math.floor((totalSeconds % 1) * fps);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
  };

  // Parse timecode string to seconds - more lenient parsing
  const parseTimecode = (timecode: string): number => {
    if (!timecode || timecode.trim() === '') return 0;
    
    // Remove all non-digit and non-colon characters
    const cleaned = timecode.replace(/[^\d:]/g, '');
    
    // Handle partial input (e.g., "1:2" should be parsed as "01:02:00:00")
    const parts = cleaned.split(':').map(p => {
      const num = parseInt(p) || 0;
      return num;
    });
    
    // Ensure we have 4 parts, pad with zeros
    while (parts.length < 4) {
      parts.push(0);
    }
    
    const [hours, minutes, seconds, frames] = parts.slice(0, 4);
    const totalSeconds = hours * 3600 + minutes * 60 + seconds + frames / fps;
    return totalSeconds;
  };

  // Initialize local value from prop only when not editing
  useEffect(() => {
    if (!isEditing && !isFocused) {
      setLocalValue(formatTimecode(value));
    }
  }, [value, fps, isEditing, isFocused]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      stopRepeat();
    };
  }, []);

  // Determine which part of timecode cursor is in (0=hours, 1=minutes, 2=seconds, 3=frames)
  const getCursorPart = (pos: number, value: string): number => {
    const colons: number[] = [];
    for (let i = 0; i < value.length; i++) {
      if (value[i] === ':') {
        colons.push(i);
      }
    }
    
    if (colons.length === 0) return 0; // No colons yet, assume hours
    if (pos <= colons[0]) return 0; // Hours (before first colon)
    if (colons.length > 1 && pos <= colons[1]) return 1; // Minutes (between first and second colon)
    if (colons.length > 2 && pos <= colons[2]) return 2; // Seconds (between second and third colon)
    return 3; // Frames (after third colon)
  };

  /** Inclusive start / exclusive end of a HH/MM/SS/FF segment in a formatted string */
  const getPartBounds = (part: number, value: string): { start: number; end: number } => {
    const colons: number[] = [];
    for (let i = 0; i < value.length; i++) {
      if (value[i] === ':') colons.push(i);
    }
    if (part === 0) return { start: 0, end: colons[0] ?? value.length };
    if (part === 1) return { start: (colons[0] ?? -1) + 1, end: colons[1] ?? value.length };
    if (part === 2) return { start: (colons[1] ?? -1) + 1, end: colons[2] ?? value.length };
    return { start: (colons[2] ?? -1) + 1, end: value.length };
  };

  const selectPart = (part: number, value: string) => {
    if (!inputRef.current) return;
    const { start, end } = getPartBounds(part, value);
    inputRef.current.setSelectionRange(start, end);
    setCursorPosition(start);
  };

  const getLivePart = (): number => {
    const el = inputRef.current;
    if (!el) return getCursorPart(cursorPosition, localValue);
    // Prefer selectionStart; if a range is selected, use midpoint of selection
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    const pos = end > start ? Math.floor((start + end - 1) / 2) : start;
    return getCursorPart(pos, localValue);
  };

  // Get value range for a specific part
  const getPartRange = (part: number): { min: number; max: number } => {
    switch (part) {
      case 0: return { min: 0, max: 99 }; // Hours
      case 1: return { min: 0, max: 59 }; // Minutes
      case 2: return { min: 0, max: 59 }; // Seconds
      case 3: return { min: 0, max: fps - 1 }; // Frames
      default: return { min: 0, max: 0 };
    }
  };

  // Get part name for display
  const getPartName = (part: number): string => {
    switch (part) {
      case 0: return 'Hours';
      case 1: return 'Minutes';
      case 2: return 'Seconds';
      case 3: return 'Frames';
      default: return '';
    }
  };

  // Adjust value at cursor position with optional step size and level progression
  const adjustValue = (delta: number, stepSize: number = 1) => {
    const part = getLivePart();
    const range = getPartRange(part);

    // Use current localValue for parsing
    const currentSeconds = parseTimecode(localValue);
    const hours = Math.floor(currentSeconds / 3600);
    const minutes = Math.floor((currentSeconds % 3600) / 60);
    const secs = Math.floor(currentSeconds % 60);
    const frames = Math.floor((currentSeconds % 1) * fps);
    
    let newHours = hours;
    let newMinutes = minutes;
    let newSecs = secs;
    let newFrames = frames;
    
    switch (part) {
      case 0: // Hours
        newHours = Math.max(range.min, Math.min(range.max, hours + delta * stepSize));
        break;
      case 1: // Minutes
        const newMinutesValue = minutes + delta * stepSize;
        if (newMinutesValue > range.max) {
          // Overflow: add to hours and reset minutes
          newHours = Math.min(99, hours + 1);
          newMinutes = 0;
        } else if (newMinutesValue < range.min) {
          // Underflow: subtract from hours and set to max minutes
          newHours = Math.max(0, hours - 1);
          newMinutes = range.max;
        } else {
          newMinutes = newMinutesValue;
        }
        break;
      case 2: // Seconds - ensure proper handling
        const newSecsValue = secs + delta * stepSize;
        if (newSecsValue > range.max) {
          // Overflow: add to minutes and reset seconds
          newMinutes = Math.min(59, minutes + 1);
          newSecs = 0;
        } else if (newSecsValue < range.min) {
          // Underflow: subtract from minutes and set to max seconds
          if (minutes > 0) {
            newMinutes = minutes - 1;
            newSecs = range.max;
          } else if (hours > 0) {
            // If minutes is 0, subtract from hours
            newHours = hours - 1;
            newMinutes = 59;
            newSecs = range.max;
          } else {
            // At absolute zero, can't go below
            newSecs = 0;
          }
        } else {
          newSecs = newSecsValue;
        }
        break;
      case 3: // Frames
        const newFramesValue = frames + delta * stepSize;
        if (newFramesValue > range.max) {
          // Overflow: add to seconds and reset frames
          if (secs < 59) {
            newSecs = secs + 1;
            newFrames = 0;
          } else {
            // Seconds overflow to minutes
            newMinutes = Math.min(59, minutes + 1);
            newSecs = 0;
            newFrames = 0;
          }
        } else if (newFramesValue < range.min) {
          // Underflow: subtract from seconds and set to max frames
          if (secs > 0) {
            newSecs = secs - 1;
            newFrames = range.max;
          } else if (minutes > 0) {
            // If seconds is 0, subtract from minutes
            newMinutes = minutes - 1;
            newSecs = 59;
            newFrames = range.max;
          } else if (hours > 0) {
            // If minutes is also 0, subtract from hours
            newHours = hours - 1;
            newMinutes = 59;
            newSecs = 59;
            newFrames = range.max;
          } else {
            // At absolute zero, can't go below
            newFrames = 0;
          }
        } else {
          newFrames = newFramesValue;
        }
        break;
    }
    
    const newSeconds = newHours * 3600 + newMinutes * 60 + newSecs + newFrames / fps;
    const newFormatted = formatTimecode(newSeconds);
    
    // Update local value immediately for visual feedback
    setLocalValue(newFormatted);
    
    // Call onChange with new value - this will trigger debounce in parent
    onChange(newSeconds);
    
    // Keep the whole segment selected so ↑/↓ continue on the same part
    requestAnimationFrame(() => {
      selectPart(part, newFormatted);
    });
  };

  // Stop repeat interval
  const stopRepeat = () => {
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current);
      repeatIntervalRef.current = null;
    }
    if (repeatTimeoutRef.current) {
      clearTimeout(repeatTimeoutRef.current);
      repeatTimeoutRef.current = null;
    }
    isKeyHeldRef.current = false;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setIsEditing(true);
    setLocalValue(newValue);
    setCursorPosition(e.target.selectionStart || 0);
    
    // Parse and update parent - allow partial input during editing
    const seconds = parseTimecode(newValue);
    onChange(seconds);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const stepSize = 1;
      adjustValue(1, stepSize);
      
      // Start repeat after initial delay
      if (!isKeyHeldRef.current) {
        isKeyHeldRef.current = true;
        
        // Initial delay before starting repeat (500ms)
        repeatTimeoutRef.current = setTimeout(() => {
          // Fast repeat interval (50ms for rapid changes)
          repeatIntervalRef.current = setInterval(() => {
            adjustValue(1, stepSize);
          }, 50);
        }, 500);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const stepSize = 1;
      adjustValue(-1, stepSize);
      
      // Start repeat after initial delay
      if (!isKeyHeldRef.current) {
        isKeyHeldRef.current = true;
        
        // Initial delay before starting repeat (500ms)
        repeatTimeoutRef.current = setTimeout(() => {
          // Fast repeat interval (50ms for rapid changes)
          repeatIntervalRef.current = setInterval(() => {
            adjustValue(-1, stepSize);
          }, 50);
        }, 500);
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // Stop any ongoing repeat
      stopRepeat();
      
      // Update cursor position on arrow keys
      setTimeout(() => {
        if (inputRef.current) {
          setCursorPosition(inputRef.current.selectionStart || 0);
        }
      }, 0);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      // Stop any ongoing repeat
      stopRepeat();
      
      // Format on Enter/Tab
      const seconds = parseTimecode(localValue);
      const formatted = formatTimecode(seconds);
      setLocalValue(formatted);
      setIsEditing(false);
    } else {
      // Stop repeat on any other key
      stopRepeat();
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      // Stop repeat when key is released
      stopRepeat();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    setCursorPosition(e.target.selectionStart || 0);
    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setIsEditing(false);
    
    // Stop any ongoing repeat
    stopRepeat();
    
    // Format value on blur
    const seconds = parseTimecode(localValue);
    const formatted = formatTimecode(seconds);
    setLocalValue(formatted);
    
    if (onBlur) {
      onBlur();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (inputRef.current) {
        setCursorPosition(inputRef.current.selectionStart || 0);
      }
    }, 0);
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    const part = getLivePart();
    const formatted = formatTimecode(parseTimecode(localValue));
    if (localValue !== formatted) {
      setLocalValue(formatted);
      onChange(parseTimecode(formatted));
    }
    requestAnimationFrame(() => {
      selectPart(part, formatted);
    });
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (inputRef.current) {
        setCursorPosition(inputRef.current.selectionStart || 0);
      }
    }, 0);
  };

  // Determine if we should show arrow indicators
  const currentPart = isFocused ? getCursorPart(cursorPosition, localValue) : -1;
  const showArrows = isFocused && currentPart >= 0;
  const partRange = currentPart >= 0 ? getPartRange(currentPart) : null;
  
  // Calculate if we can increment/decrement based on current part
  // With level progression, we can always increment/decrement except at absolute limits
  let canIncrement = false;
  let canDecrement = false;
  
  if (partRange && currentPart >= 0) {
    const currentSeconds = parseTimecode(localValue);
    const hours = Math.floor(currentSeconds / 3600);
    const minutes = Math.floor((currentSeconds % 3600) / 60);
    const secs = Math.floor(currentSeconds % 60);
    const frames = Math.floor((currentSeconds % 1) * fps);
    
    switch (currentPart) {
      case 0: // Hours - only limit at absolute max/min
        canIncrement = hours < partRange.max;
        canDecrement = hours > partRange.min;
        break;
      case 1: // Minutes - can always increment/decrement (will overflow to hours)
        canIncrement = true; // Can increment even at max (will add to hours)
        canDecrement = hours > 0 || minutes > 0; // Can decrement if not at absolute zero
        break;
      case 2: // Seconds - can always increment/decrement (will overflow to minutes)
        canIncrement = true; // Can increment even at max (will add to minutes)
        canDecrement = hours > 0 || minutes > 0 || secs > 0; // Can decrement if not at absolute zero
        break;
      case 3: // Frames - can always increment/decrement (will overflow to seconds)
        canIncrement = true; // Can increment even at max (will add to seconds)
        canDecrement = hours > 0 || minutes > 0 || secs > 0 || frames > 0; // Can decrement if not at absolute zero
        break;
    }
  }

  return (
    <div className="relative inline-block w-full">
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onFocus={handleFocus}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onSelect={handleSelect}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {showArrows && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex flex-col gap-0.5">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Ensure we have focus and get current cursor position
              if (inputRef.current) {
                inputRef.current.focus();
                const currentPos = inputRef.current.selectionStart || cursorPosition;
                setCursorPosition(currentPos);
                adjustValue(1, 1);
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Ensure we have focus and get current cursor position
              if (inputRef.current) {
                inputRef.current.focus();
                const currentPos = inputRef.current.selectionStart || cursorPosition;
                setCursorPosition(currentPos);
                
                // First adjustment
                adjustValue(1, 1);
                
                // Start repeat after initial delay
                if (!isKeyHeldRef.current) {
                  isKeyHeldRef.current = true;
                  
                  repeatTimeoutRef.current = setTimeout(() => {
                    repeatIntervalRef.current = setInterval(() => {
                      // Get fresh cursor position for each repeat
                      if (inputRef.current) {
                        const pos = inputRef.current.selectionStart || cursorPosition;
                        setCursorPosition(pos);
                      }
                      adjustValue(1, 1);
                    }, 50);
                  }, 500);
                }
              }
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopRepeat();
            }}
            onMouseLeave={() => {
              stopRepeat();
            }}
            className={`w-4 h-3 flex items-center justify-center text-xs transition-opacity cursor-pointer hover:opacity-100 ${
              canIncrement ? 'opacity-60 hover:opacity-100' : 'opacity-20 cursor-not-allowed'
            }`}
            style={{ fontSize: '8px' }}
            disabled={!canIncrement}
          >
            ▲
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Ensure we have focus and get current cursor position
              if (inputRef.current) {
                inputRef.current.focus();
                const currentPos = inputRef.current.selectionStart || cursorPosition;
                setCursorPosition(currentPos);
                adjustValue(-1, 1);
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Ensure we have focus and get current cursor position
              if (inputRef.current) {
                inputRef.current.focus();
                const currentPos = inputRef.current.selectionStart || cursorPosition;
                setCursorPosition(currentPos);
                
                // First adjustment
                adjustValue(-1, 1);
                
                // Start repeat after initial delay
                if (!isKeyHeldRef.current) {
                  isKeyHeldRef.current = true;
                  
                  repeatTimeoutRef.current = setTimeout(() => {
                    repeatIntervalRef.current = setInterval(() => {
                      // Get fresh cursor position for each repeat
                      if (inputRef.current) {
                        const pos = inputRef.current.selectionStart || cursorPosition;
                        setCursorPosition(pos);
                      }
                      adjustValue(-1, 1);
                    }, 50);
                  }, 500);
                }
              }
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
              stopRepeat();
            }}
            onMouseLeave={() => {
              stopRepeat();
            }}
            className={`w-4 h-3 flex items-center justify-center text-xs transition-opacity cursor-pointer hover:opacity-100 ${
              canDecrement ? 'opacity-60 hover:opacity-100' : 'opacity-20 cursor-not-allowed'
            }`}
            style={{ fontSize: '8px' }}
            disabled={!canDecrement}
          >
            ▼
          </button>
        </div>
      )}
    </div>
  );
};

export default TimecodeInput;
